import { deleteCookie, getCookie } from '@/app/actions/cookies';
import ModalDirective from '@/components/modal-directive';
import { siteConfig } from '@/config/site';
import axios, {
  Axios,
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { toast } from 'sonner';

const clientRequest: Axios = axios.create({
  timeout: 600000,
  baseURL: process.env.NEXT_PUBLIC_API_BASE_URL,
});

let isTokenInvalid = false;
let cachedToken: string | null = null;

async function getAuthToken(): Promise<string> {
  if (cachedToken) return cachedToken;
  const token = await getCookie(siteConfig.tokenName);
  if (token) cachedToken = token;
  return token;
}

export function clearCachedToken() {
  cachedToken = null;
}

export const showLoginModal = (platform?: string) => {
  if (!isTokenInvalid) {
    isTokenInvalid = true;
    const callback = encodeURIComponent(
      `${location.pathname}${location.search}`,
    );

    if (platform === 'mp-weixin') {
      ModalDirective.open({
        title: '认证失败，请重试',
        content: '请返回小程序重新进入该页面',
        onOk: async () => {
          isTokenInvalid = false;
          uni.navigateBack();
        },
        onOpenChange: () => {
          isTokenInvalid = false;
        },
      });
    } else {
      ModalDirective.open({
        title: '提示',
        content: '登录失效，请重新登录',
        onOk: async () => {
          isTokenInvalid = false;
          clearCachedToken();
          await deleteCookie(siteConfig.tokenName);
          window.location.href = `/login?callback=${callback}`;
        },
        onOpenChange: () => {
          isTokenInvalid = false;
        },
      });
    }
  }
};

clientRequest.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = await getAuthToken();
    if (token) {
      config.headers['X-Auth-Token'] = token;
    }

    return config;
  },
);

clientRequest.interceptors.response.use(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (response: AxiosResponse<DataResponse<unknown>>): any => {
    // 二进制数据则直接返回
    if (
      response.request.responseType === 'blob' ||
      response.request.responseType === 'arraybuffer' ||
      response.request.responseType === 'stream' ||
      response.request.responseType === 'text'
    ) {
      return response;
    }
    if (!response.data || response.data.code !== 0) {
      const msg = response.data?.msg || '请求失败';
      toast.error('请求失败', {
        position: 'top-center',
        description: msg,
      });
      const err = new Error(msg);
      (err as any).code = response.data?.code;
      return Promise.reject(err);
    }

    return response.data;
  },
  (error: AxiosError<{ msg?: string }>): Promise<never> => {
    // 用户主动取消请求，静默处理
    if (error.code === 'ERR_CANCELED') {
      return Promise.reject(new Error('Request canceled'));
    }

    // 网络断开
    if (error.code === 'ERR_NETWORK') {
      toast.error('请求失败', {
        description: '网络连接失败，请检查网络',
        position: 'top-center',
      });
      return Promise.reject(new Error('网络连接失败，请检查网络'));
    }

    // 请求超时
    if (error.code === 'ECONNABORTED') {
      toast.error('请求失败', {
        description: '请求超时',
        position: 'top-center',
      });
      return Promise.reject(new Error('请求超时'));
    }

    const { message: msg, response, config } = error;
    let content = msg;
    if (response && response.status === 504) {
      content = 'Gateway Error';
    } else if (msg.includes('Request failed with status code')) {
      if (response) {
        if (response.status === 401 || response.status === 403) {
          showLoginModal(config?.headers['X-Platform'] as string | undefined);
          return Promise.reject(new Error('Unauthorized'));
        }
        if (response.status === 404) {
          content = 'Not Found';
        } else {
          content = response.data?.msg || response.statusText;
        }
      } else {
        content = 'Unknown Error';
      }
    }

    toast.error('请求失败', {
      description: content,
      position: 'top-center',
    });

    return Promise.reject(new Error(content));
  },
);

export default clientRequest;
